import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { reels } from '../../data/reels';
import { site } from '../../data/site';
import SectionHeading from '../ui/SectionHeading';
import Reveal from '../ui/Reveal';
import { IconArrowLeft, IconArrowRight, IconClose, IconPlay } from '../ui/Icons';

/** Vertical video player, opened from a reel card. */
function ReelPlayer({ list, index, onClose, onIndex }) {
  const videoRef = useRef(null);
  const reel = list[index];

  const go = useCallback(
    (d) => onIndex((index + d + list.length) % list.length),
    [index, list, onIndex]
  );

  useEffect(() => {
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') go(1);
      if (e.key === 'ArrowLeft') go(-1);
    };
    document.addEventListener('keydown', onKey);

    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose, go]);

  // Autoplay each reel as it becomes the active one.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = 0;
    const p = v.play();
    if (p?.catch) p.catch(() => {}); // autoplay may be blocked — controls are visible
  }, [index]);

  if (!reel) return null;

  return (
    <div className="fixed inset-0 z-[110] flex flex-col bg-royal-900/95" role="dialog" aria-modal="true">
      <div className="flex shrink-0 items-center justify-between px-5 py-4 sm:px-8">
        <div>
          <p className="font-display text-lg text-cream">{reel.title}</p>
          <p className="text-[13px] uppercase tracking-wider2 text-gold-300">
            {index + 1} / {list.length}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close reel"
          className="flex h-10 w-10 items-center justify-center text-cream/70 transition-colors hover:text-gold"
        >
          <IconClose className="h-5 w-5" />
        </button>
      </div>

      <div className="relative flex min-h-0 flex-1 items-center justify-center px-3 pb-6 sm:px-16">
        <button
          type="button"
          onClick={() => go(-1)}
          aria-label="Previous reel"
          className="absolute left-1 z-10 flex h-11 w-11 items-center justify-center bg-cream/10 text-cream backdrop-blur transition-colors hover:bg-gold hover:text-royal sm:left-5"
        >
          <IconArrowLeft className="h-5 w-5" />
        </button>

        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video
          ref={videoRef}
          key={reel.src}
          src={reel.src}
          poster={reel.poster}
          controls
          playsInline
          preload="auto"
          className="max-h-full w-auto max-w-full bg-black"
        />

        <button
          type="button"
          onClick={() => go(1)}
          aria-label="Next reel"
          className="absolute right-1 z-10 flex h-11 w-11 items-center justify-center bg-cream/10 text-cream backdrop-blur transition-colors hover:bg-gold hover:text-royal sm:right-5"
        >
          <IconArrowRight className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}

/**
 * Width is owned by whatever lays the card out: the home rail sizes its
 * wrapper, the /reels page uses grid cells. Keeping a fixed `vw` width on the
 * card itself made it overflow the grid on narrow screens.
 */
export function ReelCard({ reel, onPlay, index }) {
  return (
    <button
      type="button"
      onClick={() => onPlay(index)}
      className="group relative block w-full overflow-hidden bg-royal-800"
      aria-label={`Play reel: ${reel.title}`}
    >
      <div className="relative aspect-[9/16] overflow-hidden">
        <img
          src={reel.poster}
          alt=""
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover transition-transform duration-[1400ms] ease-luxe group-hover:scale-[1.06]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-royal-900/85 via-transparent to-royal-900/15 transition-opacity duration-700 group-hover:opacity-80" />

        {/* Play affordance — a hairline ring, not a button */}
        <span className="absolute left-1/2 top-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-cream/40 text-cream transition-all duration-700 ease-luxe group-hover:scale-110 group-hover:border-gold group-hover:text-gold">
          <IconPlay className="ml-0.5 h-4 w-4" />
        </span>

        {/* Caption */}
        <div className="absolute inset-x-0 bottom-0 p-5 text-left">
          <span className="text-[11.5px] tracking-wider2 text-gold-300">
            {String(index + 1).padStart(2, '0')}
          </span>
          <h3 className="mt-1.5 font-display text-[1.15rem] leading-tight text-cream">{reel.title}</h3>
          <p className="mt-1 text-[13px] font-light leading-snug text-cream/75">{reel.caption}</p>
        </div>
      </div>
    </button>
  );
}

export default function ReelsSection() {
  const [playing, setPlaying] = useState(null);
  const scroller = useRef(null);

  const nudge = (dir) => {
    const el = scroller.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.8), behavior: 'smooth' });
  };

  return (
    <section className="relative overflow-hidden bg-royal py-20 lg:py-28">
      <div className="pointer-events-none absolute inset-0 paisley-bg opacity-[0.16]" />
      <div className="pointer-events-none absolute -left-32 top-1/3 h-[420px] w-[420px] rounded-full bg-gold/[0.06] blur-3xl" />

      <div className="relative">
        <div className="shell">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
            <SectionHeading
              index="05"
              tone="light"
              eyebrow="Reels"
              title="Watch it move"
              lede="Gold reads differently on video. Here is the collection catching light — straight from our Instagram."
            />

            <Reveal className="flex shrink-0 items-center gap-3">
              <button
                type="button"
                onClick={() => nudge(-1)}
                aria-label="Scroll reels left"
                className="flex h-11 w-11 items-center justify-center border border-cream/25 text-cream/70 transition-all hover:border-gold hover:bg-gold hover:text-royal lg:hidden"
              >
                <IconArrowLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => nudge(1)}
                aria-label="Scroll reels right"
                className="flex h-11 w-11 items-center justify-center border border-cream/25 text-cream/70 transition-all hover:border-gold hover:bg-gold hover:text-royal lg:hidden"
              >
                <IconArrowRight className="h-4 w-4" />
              </button>

              <a
                href={site.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-line-light"
              >
                <span className="btn-line-rule" />
                @pramukhjewellers
              </a>
            </Reveal>
          </div>
        </div>

        {/* Rail: horizontal snap on mobile, grid on desktop */}
        <div
          ref={scroller}
          className="no-scrollbar mt-12 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 pb-2 sm:px-8 lg:mx-auto lg:grid lg:max-w-[1360px] lg:grid-cols-6 lg:gap-5 lg:overflow-visible lg:px-12"
        >
          {reels.map((r, i) => (
            <div key={r.id} className="w-[74vw] shrink-0 snap-start sm:w-[300px] lg:w-auto">
              <Reveal delay={i * 70}>
                <ReelCard reel={r} index={i} onPlay={setPlaying} />
              </Reveal>
            </div>
          ))}
        </div>

        <div className="shell mt-10 text-center">
          <Link to="/reels" className="btn-line-light">
            <span className="btn-line-rule" />
            View all reels
          </Link>
        </div>
      </div>

      {playing !== null && (
        <ReelPlayer list={reels} index={playing} onIndex={setPlaying} onClose={() => setPlaying(null)} />
      )}
    </section>
  );
}

export { ReelPlayer };
